import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import TableCell from "../../common/ui/TableCell";
import { updateQualityMeta } from "../../../api/quality/meta";
import { getDocSignatureInfo } from "../../../api/papers";

import SignTableQuality from "../../approval/SignTableQuality";

/* ✅ 드롭다운 옵션 */
const ResultOptions = () => (
  <>
    <option value="">선택</option>
    <option value="OK">적합 (OK)</option>
    <option value="NG">부적합 (NG)</option>
    <option value="NA">해당없음 (NA)</option>
  </>
);

export default function QualityPCView({ data }) {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const [lines, setLines] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [pmSignExists, setPmSignExists] = useState(false);

  // 1. 데이터 동기화
  useEffect(() => {
    if (data?.lines || data?.items) {
      setLines(data.lines || data.items || []);
    }
  }, [data]);

  /* ===============================
     기본 데이터
  =============================== */
  const navigate = useNavigate();
  const header = data?.header || {};
  const dns = data?.dns || data?.doc_dns;
  const miDns = data?.mi_dns || header.mi_dns; // ✅ Linked MI DNS
  const assignedPeople = header.assigned_people || [];
  const pmId = assignedPeople[1];
  const myId = String(user.id || "");

  // 2. PM 서명 여부 확인 (권한 체크용)
  useEffect(() => {
    if (!data?.dns || !data?.project || !pmId) return;
    getDocSignatureInfo(data.dns, data.project, pmId)
      .then((res) => setPmSignExists(!!res.data?.sign))
      .catch(() => setPmSignExists(false));
  }, [data, pmId]);

  // 3. 권한 로직
  // PM이고 + (서명이 없거나 OR 점검일자가 비어있으면) => 수정 가능
  const isPmUser = pmId && myId === String(pmId);
  const isAlreadySigned = !!(header.is_signed || header.approval_date);
  const isEditable = isPmUser && !isAlreadySigned;

  // 4. 핸들러 (체크리스트 수정)
  const handlePmChange = (idx, field, value) => {
    if (!isEditable) return;
    const nextLines = [...lines];
    nextLines[idx] = { ...nextLines[idx], [field]: value };
    setLines(nextLines);
  };

  // 5. 저장 핸들러
  const handleSavePmCheck = async () => {
    if (!window.confirm("승인 하시겠습니까?\n승인 후에는 수정이 불가능합니다."))
      return;

    setIsSaving(true);
    try {
      const today = new Date().toISOString().slice(0, 10);

      // (0) 내 서명 가져오기 (로컬 스토리지 user 정보 활용)
      const mySign = user.sign || null;

      // (1) 헤더 업데이트 (날짜 + 서명 + 승인 플래그)
      const currentInfo = header.assigned_people_info || {};
      const currentPm = currentInfo.pm || {};

      const newHeader = {
        ...header,
        check_date: today,
        approval_date: today,
        is_signed: true,
        assigned_people_info: {
          ...currentInfo,
          pm: {
            ...currentPm,
            sign: mySign, // 서명 저장
          },
        },
      };

      console.log("Save Checklist - DNS:", dns);
      if (!dns) {
        alert("문서 번호(DNS)를 찾을 수 없습니다.");
        setIsSaving(false);
        return;
      }

      console.log("Save Checklist - Payload Header:", newHeader);

      // (2) 메타 데이터 업데이트
      await updateQualityMeta(dns, {
        ...data,
        header: newHeader,
        lines: lines,
      });

      alert("✅ 승인 및 저장이 완료되었습니다.");
      window.location.reload();
    } catch (e) {
      console.error("Save Error:", e);
      alert("저장 중 오류가 발생했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  /* UI 렌더링 헬퍼 */
  const getResultColor = (val) => {
    if (val === "OK") return "text-blue-600 font-bold";
    if (val === "NG") return "text-red-600 font-bold";
    return "text-gray-500";
  };
  const getResultLabel = (val) => {
    if (val === "OK") return "적합";
    if (val === "NG") return "부적합";
    if (val === "NA") return "해당없음";
    return val || "-";
  };
  const getTypeLabel = (val) =>
    val === "SPEC" ? "시방" : val === "DRAWING" ? "도면" : val || "-";

  if (!data) return null;

  return (
    <div className="space-y-6 bg-white">
      {/* 기본 정보 */}
      <table className="doc-table w-full text-sm mb-4">
        <tbody>
          <tr>
            <TableCell label="작성자" value={header.writer || "-"} />
            <TableCell label="작성일자" value={header.write_date || "-"} />
          </tr>
          <tr>
            <TableCell
              label="MI 요청서"
              value={header.mi_dns || "-"}
              colSpan={3}
            />
          </tr>
          <tr>
            <TableCell
              label="제목"
              value={data.title || header.title || "-"}
              colSpan={3}
            />
          </tr>
        </tbody>
      </table>

      {/* 상세 정보 */}
      <table className="doc-table w-full text-sm mb-6">
        <tbody>
          <tr>
            <TableCell label="공종" value={header.code_no || "-"} />
            <TableCell label="Rev" value={header.rev_no || "-"} />
          </tr>
          <tr>
            <TableCell label="위치" value={header.location_place || "-"} />
            <TableCell label="규정" value={header.apply_regulation || "-"} />
          </tr>
        </tbody>
      </table>

      {/* 체크리스트 */}
      <div className="flex justify-between items-end mb-2">
        <div className="text-body-bold">검사 항목</div>
        {/* {isPmUser && (
          <span
            className={`text-xs font-bold ${
              isEditable ? "text-blue-600" : "text-green-600"
            }`}
          >
            {isEditable ? "* 결과 입력 가능 (승인 시 저장됨)" : "✓ 승인 완료됨"}
          </span>
        )} */}
      </div>

      <div className="overflow-x-auto border-t border-black">
        <table className="doc-table w-full text-sm text-center">
          <thead className="doc-thead bg-gray-100">
            <tr>
              <th className="doc-th w-1/4" rowSpan={3}>
                검사항목
              </th>
              <th className="doc-th w-1/4" rowSpan={3}>
                검사기준
              </th>
              <th className="doc-th" colSpan={5}>
                결과
              </th>
              <th className="doc-th-end w-1/6" rowSpan={3}>
                비고
              </th>
            </tr>
            <tr>
              <th className="doc-th-result">구분</th>
              <th className="doc-th-result" colSpan={2}>
                시공사
              </th>
              <th className="doc-th-result" colSpan={2}>
                PM
              </th>
            </tr>
            <tr>
              <th className="doc-th-result w-14">타입</th>
              <th className="doc-th-result w-14">1차</th>
              <th className="doc-th-result w-14">2차</th>
              <th className="doc-th-result w-14">1차</th>
              <th className="doc-th-result w-14">2차</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-6">
                  내용 없음
                </td>
              </tr>
            ) : (
              lines.map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="doc-td text-left px-2 whitespace-pre-wrap">
                    {row.item}
                  </td>
                  <td className="doc-td text-left px-2 whitespace-pre-wrap">
                    {row.criteria}
                  </td>
                  <td className="doc-td">{getTypeLabel(row.criteria_type)}</td>

                  <td className={`doc-td ${getResultColor(row.s_result_1)}`}>
                    {getResultLabel(row.s_result_1)}
                  </td>
                  <td className={`doc-td ${getResultColor(row.s_result_2)}`}>
                    {getResultLabel(row.s_result_2)}
                  </td>

                  <td
                    className={`doc-td ${
                      !isEditable ? getResultColor(row.pm_result_1) : "p-0"
                    }`}
                  >
                    {isEditable ? (
                      <select
                        className="w-full h-full border-none text-center text-xs bg-blue-50/50 cursor-pointer"
                        value={row.pm_result_1 || ""}
                        onChange={(e) =>
                          handlePmChange(idx, "pm_result_1", e.target.value)
                        }
                      >
                        <ResultOptions />
                      </select>
                    ) : (
                      getResultLabel(row.pm_result_1)
                    )}
                  </td>
                  <td
                    className={`doc-td ${
                      !isEditable ? getResultColor(row.pm_result_2) : "p-0"
                    }`}
                  >
                    {isEditable ? (
                      <select
                        className={`w-full h-full border-none text-center text-xs ${
                          row.pm_result_1 === "NG"
                            ? "bg-blue-50/50 cursor-pointer"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        }`}
                        value={row.pm_result_2 || ""}
                        disabled={row.pm_result_1 !== "NG"}
                        onChange={(e) =>
                          handlePmChange(idx, "pm_result_2", e.target.value)
                        }
                      >
                        <ResultOptions />
                      </select>
                    ) : (
                      getResultLabel(row.pm_result_2)
                    )}
                  </td>
                  <td className="doc-td text-left px-2">{row.action}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 결재 승인 테이블 */}
      <SignTableQuality data={data} />

      {/* 저장 버튼 */}
      {isEditable && (
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSavePmCheck}
            disabled={isSaving}
            className="btn-save-sm "
          >
            {isSaving ? "처리 중..." : "결재 승인 (완료)"}
          </button>
        </div>
      )}
      {/* ✅ 승인 완료 시, 통보서 작성 페이지(MI View)로 이동 버튼 */}
      {isAlreadySigned && miDns && (
        <div className="flex justify-end mt-8 border-t pt-4">
          <button
            className="btn-basic-sm"
            onClick={() =>
              navigate(`/project/${data.project}/quality/request/${miDns}/view`)
            }
          >
            시공 품질 검수 결과 통보서 작성하기
          </button>
        </div>
      )}
    </div>
  );
}
